import * as borogove from "borogove";
import { CircularProgress, Button, Stack } from "@mui/material";
import { useQueryState } from "nuqs";
import {
	Await,
	useNavigate,
	useOutletContext,
	useAsyncValue,
	useAsyncError,
} from "react-router";
import { Suspense, useState, FormEvent } from "react";

import { Context, Accounts } from "../App";
import { FormSection } from "./Form";
import { newAccount } from "./Login";
import { href } from "../util";

const RegisterInner = () => {
	const navigate = useNavigate();
	const { setAccounts, persistence, idbp } = useOutletContext<Context>();
	const [register, form] = useAsyncValue() as [
		borogove.Register,
		borogove.Form,
	];
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		const data = new FormData(e.currentTarget);
		try {
			const accountId = await register.submit(data);
			if (accountId) {
				const account = newAccount(accountId, idbp, persistence);
				if (data.has("password")) {
					const token = account.client.addPasswordNeededListener(() => {
						account.client.usePassword("" + data.get("password"));
						account.client.removeEventListener(token);
					});
				}
				setAccounts((accounts: Accounts) => ({
					...accounts,
					[accountId]: account,
				}));
				navigate(href("/accounts/:accountId", { accountId }));
			} else {
				navigate("/login");
			}
		} catch (ex) {
			console.error(ex);
			setError("" + ex);
		}
	};

	return form.url() ? (
		<a style={{ margin: "auto" }} href={form.url() ?? undefined}>
			{form.title() ? form.title() : "click here"}
		</a>
	) : (
		<form style={{ margin: "auto" }} method="dialog" onSubmit={handleSubmit}>
			<Stack spacing={1}>
				<FormSection section={form} />
				{error && <p>Error: {error}</p>}
				<Button type="submit" variant="contained">
					Register
				</Button>
			</Stack>
		</form>
	);
};

const RegisterError = () => {
	const error = useAsyncError();

	return (
		<p style={{ margin: "auto" }}>Error connecting to server: {"" + error}</p>
	);
};

const registerAndForm = async (domainOrInvite: string) => {
	const register = await borogove.Register.fromDomainOrInvite(domainOrInvite);
	const form = await register.getForm();
	return [register, form[0]];
};

export const Register = () => {
	const [with_] = useQueryState("with");
	const promise =
		with_ == null
			? Promise.reject("No server or invite provided")
			: registerAndForm(with_);

	return (
		<Suspense
			key={with_}
			fallback={<CircularProgress className="fullscreen" />}
		>
			<Await resolve={promise} errorElement={<RegisterError />}>
				<RegisterInner />
			</Await>
		</Suspense>
	);
};
